/**
 * Shared data-shape helpers for WAT / ORB configurations.
 *
 * Each renderer (Pro strategy modal, Classic position table, compact card,
 * strategy leg card) keeps its own layout — these helpers return a structured
 * list of `{label, value}` items so the UI stays in sync with config semantics
 * when fields change (e.g. new wait_and_trade keys). Callers decide how much
 * of the returned list to show.
 */

import { UL_WAT_TYPES } from '../constants/tradingConstants';

export interface TriggerItem {
  label: string;
  value: string;
}

export interface WatConfigShape {
  type: string;
  value: number;
  trigger: number;
  candleClose?: number;
  offset?: number;
  // UL W&T only — backend `_normalize_wat_config` UL branch emits these.
  // spotTrigger is the trigger in spot terms (index points), referenceSpot
  // is the spot at which the position was configured, tradeOnlyFirstEntry
  // echoes the leg-config flag (re-entry cross-back semantic).
  spotTrigger?: number;
  referenceSpot?: number;
  tradeOnlyFirstEntry?: boolean;
}

export interface OrbConfigShape {
  rangeHigh: number;
  rangeLow: number;
  rangeStart: string;
  rangeEnd: string;
  breakoutSide: string;
}

/** POINTS → PTS, PERCENTAGE → PCT, UL_POINTS → UL PTS, UL_PERCENTAGE → UL PCT
 *  (plus the UL W&T direction variants). Used by compact callouts.
 *  Lookup-map form (Phase 6 review nit L3) — mechanical mapping. */
const SHORT_TYPE_LABELS: Record<string, string> = {
  POINTS:         'PTS',
  PERCENTAGE:     'PCT',
  UL_POINTS:      'UL PTS',
  UL_PERCENTAGE:  'UL PCT',
  UL_POINTS_UP:   'UL ↑ PTS',
  UL_POINTS_DOWN: 'UL ↓ PTS',
  UL_PCT_UP:      'UL ↑ PCT',
  UL_PCT_DOWN:    'UL ↓ PCT',
};

export function shortRiskType(type: string): string {
  return SHORT_TYPE_LABELS[type] ?? type;
}

// UL W&T types are index-spot-driven — the offset unit is spot points/percent
// and the trigger is a spot level, not a premium price. Use the canonical
// UL_WAT_TYPES set (Phase 6 review M1) — the prior `startsWith('UL_')` was
// over-broad (would match UL_POINTS/UL_PERCENTAGE risk types if they ever
// leaked into a WAT-shaped cfg) and threw on undefined. Nullish-safe.
const isUlWatType = (t: string | undefined | null): boolean =>
  UL_WAT_TYPES.includes(t ?? '');

// Percentage-family WAT types — needed for the '%' unit suffix on the Offset
// row. Includes premium PERCENTAGE + both UL_PCT directions (Phase 6 M1 nit
// found via lens 6: the inline `type === 'UL_PCT_UP' || 'UL_PCT_DOWN'` was
// drift-prone).
const PERCENT_WAT_TYPES: readonly string[] = [
  'PERCENTAGE',
  'UL_PCT_UP',
  'UL_PCT_DOWN',
];

/**
 * Describe a wait-and-trade config as labeled items. `entryTriggerPrice` is
 * the authoritative stored trigger on the position; falls back to it when
 * `cfg.trigger` hasn't been computed yet. UL W&T rows render `Reference` +
 * `Spot trigger` (in spot terms) instead of the premium trigger, since UL
 * W&T rests no premium order and the field the user watches is the spot.
 */
export function describeWatConfig(
  cfg: WatConfigShape | null | undefined,
  entryTriggerPrice?: number | null,
): TriggerItem[] {
  if (!cfg) return [];
  const items: TriggerItem[] = [];
  const isUl = isUlWatType(cfg.type);
  const unit = PERCENT_WAT_TYPES.includes(cfg.type) ? '%' : '';
  items.push({ label: 'Offset', value: `${cfg.value}${unit} ${shortRiskType(cfg.type)}` });
  if (cfg.candleClose != null) {
    items.push({ label: 'Candle', value: `${cfg.candleClose}m close` });
  }
  if (cfg.offset != null && cfg.offset !== 0) {
    const sign = cfg.offset >= 0 ? '+' : '';
    items.push({ label: 'Shift', value: `${sign}${cfg.offset.toFixed(2)}` });
  }
  if (isUl) {
    // UL W&T: prefer spot-terms fields when the backend has stamped them.
    if (cfg.referenceSpot != null && cfg.referenceSpot > 0) {
      items.push({ label: 'Reference', value: cfg.referenceSpot.toFixed(2) });
    }
    if (cfg.spotTrigger != null && cfg.spotTrigger > 0) {
      items.push({ label: 'Spot trigger', value: cfg.spotTrigger.toFixed(2) });
    }
    if (cfg.tradeOnlyFirstEntry) {
      items.push({ label: 'Re-entry', value: 'First entry only' });
    }
  } else {
    const resolvedTrigger = cfg.trigger > 0 ? cfg.trigger : entryTriggerPrice ?? null;
    if (resolvedTrigger != null && resolvedTrigger > 0) {
      items.push({ label: 'Trigger', value: resolvedTrigger.toFixed(2) });
    }
  }
  return items;
}

/**
 * Describe an opening-range-breakout config as labeled items.
 */
export function describeOrbConfig(
  cfg: OrbConfigShape | null | undefined,
  entryTriggerPrice?: number | null,
): TriggerItem[] {
  if (!cfg) return [];
  const items: TriggerItem[] = [];
  if (cfg.rangeStart || cfg.rangeEnd) {
    items.push({
      label: 'Window',
      value: `${cfg.rangeStart || '?'} – ${cfg.rangeEnd || '?'}`,
    });
  }
  items.push({
    label: 'Range',
    value: `${cfg.rangeLow.toFixed(2)} – ${cfg.rangeHigh.toFixed(2)}`,
  });
  if (entryTriggerPrice != null && entryTriggerPrice > 0) {
    items.push({ label: 'Trigger', value: entryTriggerPrice.toFixed(2) });
  }
  return items;
}

/** Header label for the ORB section — optionally includes the breakout side. */
export function orbHeaderLabel(cfg: OrbConfigShape): string {
  return cfg.breakoutSide ? `ORB ${cfg.breakoutSide}` : 'ORB';
}
